// Context object + hook + types for the UploadSubmitProvider. Kept
// in its own non-component file so the provider file (a .tsx) only
// exports components, satisfying react-refresh/only-export-components.
import { createContext, useContext, useMemo } from "react";

export interface UploadSubmitInput {
  project_id: string;
  // Human-readable project name (the linked site's name). Shown in the
  // progress pill instead of the opaque project_id / site id.
  project_name: string;
  files: File[];
  // Already-attached uploads the operator re-picked unchanged: they are
  // referenced, not re-uploaded. A freshly-picked file with the same name
  // wins over a reuse entry (dedup by filename).
  reuse_uploads?: { upload_id: string; kind: string; original_name: string }[];
}

export type UploadSubmitStage = "uploading" | "done" | "error";

// One per concurrent submit. The provider keeps a Map of these keyed by
// submitId so uploads to several projects each track their own progress,
// and the pill stack renders one pill per state. An absent entry IS the
// idle state.
export interface UploadSubmitState {
  // Opaque per-submit id assigned by the provider. The pill uses it
  // as a React key and as the argument to `dismiss(submitId)`.
  submitId: string;
  stage: UploadSubmitStage;
  label: string;
  projectId: string;
  // Display name for the project, shown in the pill in place of the id.
  projectName: string | null;
  error: Error | null;
  uploadProgress: { current: number; total: number };
  // Wall-clock ms timestamp when this submit started, for the pill's
  // elapsed-time hint.
  submitStartedAt: number;
}

export interface UploadSubmitContextValue {
  // Active submits, in registration order. Empty when nothing is in
  // flight and nothing recently finished is awaiting dismissal.
  submits: UploadSubmitState[];
  submit: (input: UploadSubmitInput) => Promise<void>;
  // Remove a finished submit (done / error) from the stack. No-op while
  // it is still uploading.
  dismiss: (submitId: string) => void;
}

export const UploadSubmitContext =
  createContext<UploadSubmitContextValue | null>(null);

export const useUploadSubmitContext = (): UploadSubmitContextValue => {
  const ctx = useContext(UploadSubmitContext);
  if (!ctx) {
    throw new Error(
      "useUploadSubmitContext must be used inside <UploadSubmitProvider>",
    );
  }
  return ctx;
};

// The most recent submit (in registration order) for the given project,
// or null when nothing in the stack belongs to it. Lets a per-project
// surface react to its own submit rather than a global one.
export const useLatestUploadForProject = (
  projectId: string | null | undefined,
) => {
  const { submits } = useUploadSubmitContext();
  return useMemo(() => {
    if (!projectId) return null;
    for (let i = submits.length - 1; i >= 0; i--) {
      if (submits[i].projectId === projectId) return submits[i];
    }
    return null;
  }, [submits, projectId]);
};
